import React, { useCallback, useContext, useEffect, useState } from "react";
import InnerLayout from "../../../components/layouts/inner-layout";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DynamicTransactionTable from "../../../components/tables/dynamic-transaction-table";
import LoadingSpinner from "../../../components/helper-components/loading-spinner";
import { DataContext } from "../../../components/layouts/main-layout";
import moment from "moment";
import BadgesComponent from "../../../components/tables/badges-component";
import { eWayBillTableAction } from "../../../redux/actions/sales-action";
import FilterRadioButtons from "../../../components/forms/filter-radio-buttons";

/* ── EWB-specific filter: only Cancelled + Rejected ── */
const EWB_STATUS_OPTIONS = [
    { label: "Cancelled", value: "cancelled" },
    { label: "Rejected",  value: "rejected"  },
];

const EWayBillFilter = ({ filterData, setFilterData, setCurrentPage }) => {
    const handleChange = (e) => {
        setFilterData({ ...filterData, orderStatus: e.target.value });
        setCurrentPage(1);
    };
    return (
        <div
            style={{
                minWidth: "17rem",
                zIndex: 999,
                top: "40px",
                right: "-5px",
                boxShadow: "0 12px 32px -8px var(--popover-shadow-color), 0 2px 6px -2px var(--popover-shadow-color)",
            }}
            className="absolute border border-[var(--popover-border-color)] py-2 px-2 bg-[var(--popover-bg-color)] rounded-xl z-10 flex flex-col"
        >
            <span className="px-3 pt-1.5 pb-2.5 text-sm fw_600 text-[var(--popover-heading-fg-color)]">
        Filter by
      </span>
            <hr className="border-0 border-t border-[var(--popover-border-color)] -mx-2" />
            <div>
                <FilterRadioButtons
                    name="ewb_status"
                    label="Status"
                    options={EWB_STATUS_OPTIONS}
                    handleChange={handleChange}
                    value={filterData?.orderStatus}
                />
            </div>
        </div>
    );
};

const EWayBillsTable = () => {
    const { permissionFn } = useContext(DataContext);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [eWayBills, setEWayBills] = useState([]);
    const [dataCount, setDataCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [reload, setReload] = useState(false);
    const [showFilter, setShowFilter] = useState(false);

    const [filterData, setFilterData] = useState({
        sort_by: "",
        sort_order: "",
        selectColumn: "",
        from: "",
        to: "",
        inputValue: "",
        orderStatus: "",
    });

    useEffect(() => {
        dispatch(
            eWayBillTableAction(
                {
                    pageNumber: currentPage,
                    pageSize: pageSize,
                    filterData,
                },
                onSuccess,
                onError
            )
        );
    }, [currentPage, pageSize, filterData, reload]);

    const onSuccess = (data) => {
        setError(false);
        setLoading(false);
        /* handle multiple possible response shapes from the EWB list API */
        const list =
            data?.data?.list ??
            data?.data?.orderList ??
            data?.data?.ewbList ??
            (Array.isArray(data?.data) ? data.data : []);
        const count =
            data?.data?.totalCount ??
            data?.totalCount ??
            list.length;
        setEWayBills(list);
        setDataCount(count);
    };

    const onError = () => {
        setError(true);
        setLoading(false);
    };

    const paginationProps = {
        pageSize,
        setPageSize,
        dataCount,
        setCurrentPage,
        currentPage,
    };

    const handleFilterClick = useCallback(() => {
        setShowFilter((prev) => !prev);
    }, []);

    const filterby = showFilter ? (
        <EWayBillFilter
            filterData={filterData}
            setFilterData={setFilterData}
            setCurrentPage={setCurrentPage}
        />
    ) : null;

    const tableBodyData = eWayBills?.map((item) => {
        /* response may be flat or nested under sales_order / eway_bill */
        const inv  = item?.sales_order ?? item;
        const ewb  = item?.eway_bill   ?? item;

        const invNo = inv?.soit_invoice_number ?? ewb?.ewb_invoice_number ?? "--";
        const ewbNo = ewb?.ewb_eway_bill_number ?? ewb?.ewbNo ?? ewb?.ewb_no ?? null;

        const rawDate    = inv?.soit_order_date   ?? inv?.created_date;
        const rawExpiry  = ewb?.ewb_valid_upto    ?? ewb?.validUpto ?? ewb?.ewb_expiry_date;

        return {
            id: inv?.soit_id ?? item?.soit_id,
            soit_order_date: rawDate && moment(rawDate).isValid()
                ? moment(rawDate).format("MMM D, YYYY")
                : "--",
            soit_invoice_number: (
                <span className="fw_500 cursor-pointer">{invNo}</span>
            ),
            ewb_number: ewbNo ? (
                <span className="fw_500">{ewbNo}</span>
            ) : (
                <span className="text-[var(--placeholder-color)]">--</span>
            ),
            cmr_name:  inv?.cmr_name  ?? item?.cmr_name  ?? "--",
            cmr_gstin: inv?.cmr_gstin ?? item?.cmr_gstin ?? ewb?.ewb_user_gstin ?? "--",
            ewb_expiry_date: rawExpiry && moment(rawExpiry).isValid()
                ? moment(rawExpiry).format("MMM D, YYYY")
                : "--",
            soit_total_amount: "₹ " + (inv?.soit_total_amount ?? item?.soit_total_amount ?? "0.00"),
            order_status: <BadgesComponent status={inv?.soit_order_status ?? item?.soit_order_status} />,
            soit_order_status: inv?.soit_order_status ?? item?.soit_order_status ?? "",
        };
    });

    return (
        <InnerLayout
            permissionFn={permissionFn}
            module="sales_invoice"
            title="e-Way Bills"
        >
            {loading ? (
                <LoadingSpinner />
            ) : error ? (
                <></>
            ) : (
                <>
                    <DynamicTransactionTable
                        transaction="e-Way Bill"
                        transaction_type="sales_invoice"
                        setInvoiceItems={() => { }}
                        invoiceItems={[]}
                        options={[]}
                        orders={eWayBills}
                        setOrders={setEWayBills}
                        tableHeading1={tableHeading1}
                        is_checkbox_req={false}
                        onClickItemFn={(id) => navigate(`/view-e-way-bill/${id}`)}
                        list={false}
                        permissionFn={permissionFn}
                        module={"sales_invoice"}
                        path="sales-invoice"
                        reload={reload}
                        setReload={setReload}
                        loading={loading}
                        setLoading={setLoading}
                        deleteAction={() => { }}
                        label="e-Way Bill"
                        showFilter={showFilter}
                        filterFn={handleFilterClick}
                        setFilterData={setFilterData}
                        filterData={filterData}
                        paginationProps={paginationProps}
                        tableHeading={tableHeading}
                        tableBodyData={tableBodyData}
                        placeholder="Search (/)"
                        filterBy={filterby}
                        initialFilter={{
                            sort_by: "",
                            sort_order: "",
                            selectColumn: "soit_invoice_number",
                            from: "",
                            to: "",
                            inputValue: "",
                            orderStatus: "",
                        }}
                        handleSelect={() => { }}
                        transactionKey="soit"
                        actionsPermissions={{
                            edit: [],
                            delete: [],
                            print: ["open", "closed"],
                            send: ["open", "closed"],
                            clone: [],
                        }}
                        menu={true}
                    />
                </>
            )}
        </InnerLayout>
    );
};

export default EWayBillsTable;

const tableHeading = [
    {
        label: "DATE",
        value: "soit_order_date",
        display: true,
        column: true,
    },
    {
        label: "TRANSACTION #",
        value: "soit_invoice_number",
        display: true,
        column: true,
    },
    {
        label: "EWB #",
        value: "ewb_number",
        display: true,
        column: true,
    },
    {
        label: "CUSTOMER NAME",
        value: "cmr_name",
        display: true,
        column: true,
    },
    {
        label: "CUSTOMER GSTIN",
        value: "cmr_gstin",
        display: true,
        column: true,
    },
    {
        label: "VALID UPTO",
        value: "ewb_expiry_date",
        display: true,
        column: true,
    },
    {
        label: "TOTAL",
        value: "soit_total_amount",
        display: true,
        column: true,
        className: "justify-end pe-5",
    },
    {
        label: "",
        value: "",
        column: false,
    },
];

const tableHeading1 = [
    { label: "PRODUCT NAME", value: "item_name" },
    { label: "GENERIC NAME", value: "item_generic_name" },
    { label: "ITEM CODE", value: "item_code" },
    { label: "UOM", value: "item_uom" },
    { label: "PRICE (₹)", value: "item_unit_price" },
    { label: "QTY", value: "item_quantity" },
    { label: "TOTAL (₹)", value: "item_total_amount" },
];
